import { TransferState } from '../../types';

import { DEFAULT_TRANSFER_TOKEN_SLUG, IS_LEGENDS_WALLET } from '../../../config';
import { findChainConfig, getChainConfig } from '../../../util/chain';
import { fromDecimal, toDecimal } from '../../../util/decimals';
import { getChainBySlug } from '../../../util/tokens';
import { getTransferQuoteRefreshDelay } from '../../../util/transferQuote';
import { addActionHandler, setGlobal } from '../../index';
import { resetHardware, setCurrentTransferAddress, updateCurrentTransfer } from '../../reducers';
import { selectEnclaveToken, selectIsEnclaveSessionValid, selectIsHardwareAccount } from '../../selectors';

addActionHandler('startTransfer', (global, actions, payload) => {
  const { isOfframp, tokenSlug: requestedTokenSlug, ...rest } = payload ?? {};
  const currentTokenSlug = global.currentTransfer.tokenSlug;
  const isCurrentTokenSupported = Boolean(
    currentTokenSlug && findChainConfig(getChainBySlug(currentTokenSlug)),
  );
  const legendsDefaultTokenSlug = IS_LEGENDS_WALLET
    ? getChainConfig('tron').usdtSlug[global.settings.isTestnet ? 'testnet' : 'mainnet']
    : undefined;
  const tokenSlug = requestedTokenSlug
    ?? legendsDefaultTokenSlug
    ?? (isCurrentTokenSupported ? currentTokenSlug : DEFAULT_TRANSFER_TOKEN_SLUG);

  const nftTokenSlug = Symbol('nft');
  const previousFeeTokenSlug = global.currentTransfer.nfts?.length ? nftTokenSlug : global.currentTransfer.tokenSlug;
  const nextFeeTokenSlug = payload?.nfts?.length ? nftTokenSlug : tokenSlug;
  const shouldClearFee = nextFeeTokenSlug && nextFeeTokenSlug !== previousFeeTokenSlug;

  setGlobal(updateCurrentTransfer(global, {
    state: TransferState.Initial,
    error: undefined,
    isQuoteReady: false,
    ...(shouldClearFee || IS_LEGENDS_WALLET ? { explainedFee: undefined, diesel: undefined } : {}),
    sponsorship: undefined,
    repeatRequired: false,
    repeatAuthorization: undefined,
    draftRevision: (global.currentTransfer.draftRevision ?? 0) + 1,
    ...rest,
    tokenSlug,
    isOfframp,
  }));

  // For offramp mode, automatically submit to calculate fee and go to Confirm screen
  if (isOfframp && payload?.amount && payload?.toAddress) {
    actions.submitTransferInitial({
      tokenSlug,
      amount: payload.amount,
      toAddress: payload.toAddress,
      comment: payload.comment,
    });
  }
});

addActionHandler('changeTransferToken', (global, actions, { tokenSlug, withResetAmount }) => {
  const { amount, tokenSlug: currentTokenSlug, nfts } = global.currentTransfer;
  if (!nfts?.length && tokenSlug === currentTokenSlug && !withResetAmount) {
    return;
  }

  const currentToken = currentTokenSlug ? global.tokenInfo.bySlug[currentTokenSlug] : undefined;
  const newToken = global.tokenInfo.bySlug[tokenSlug];

  if (withResetAmount) {
    global = updateCurrentTransfer(global, { amount: undefined });
  } else if (amount && currentToken?.decimals !== newToken?.decimals) {
    global = updateCurrentTransfer(global, {
      amount: fromDecimal(toDecimal(amount, currentToken?.decimals), newToken?.decimals),
    });
  }

  setGlobal(updateCurrentTransfer(global, {
    tokenSlug,
    explainedFee: undefined,
    isQuoteReady: false,
    diesel: undefined,
    sponsorship: undefined,
    repeatRequired: false,
    repeatAuthorization: undefined,
    draftRevision: (global.currentTransfer.draftRevision ?? 0) + 1,
    nfts: undefined,
  }));
});

addActionHandler('setTransferScreen', (global, actions, payload) => {
  const { state } = payload;

  return updateCurrentTransfer(global, {
    state,
    ...(IS_LEGENDS_WALLET && state === TransferState.Initial && global.currentTransfer.state === TransferState.Confirm
      ? { isLoading: false, isQuoteReady: false, explainedFee: undefined, sponsorship: undefined }
      : {}),
  });
});

addActionHandler('setTransferAmount', (global, actions, { amount }) => {
  if (global.currentTransfer.amount === amount) return global;

  return updateCurrentTransfer(global, {
    amount, sponsorship: undefined, repeatRequired: false, repeatAuthorization: undefined,
    ...(IS_LEGENDS_WALLET ? { explainedFee: undefined, isQuoteReady: false } : {}),
    draftRevision: (global.currentTransfer.draftRevision ?? 0) + 1,
  });
});

addActionHandler('setTransferToAddress', (global, actions, { toAddress }) => {
  return updateCurrentTransfer(setCurrentTransferAddress(global, toAddress), {
    sponsorship: undefined, repeatRequired: false, repeatAuthorization: undefined,
    ...(IS_LEGENDS_WALLET ? { explainedFee: undefined, isQuoteReady: false } : {}),
    draftRevision: (global.currentTransfer.draftRevision ?? 0) + 1,
  });
});

addActionHandler('setTransferComment', (global, actions, { comment }) => {
  return updateCurrentTransfer(global, {
    comment, repeatRequired: false, repeatAuthorization: undefined,
    ...(IS_LEGENDS_WALLET ? { explainedFee: undefined, sponsorship: undefined, isQuoteReady: false } : {}),
    draftRevision: (global.currentTransfer.draftRevision ?? 0) + 1,
  });
});

addActionHandler('setTransferShouldEncrypt', (global, actions, { shouldEncrypt }) => {
  return updateCurrentTransfer(global, {
    shouldEncrypt, repeatRequired: false, repeatAuthorization: undefined,
    ...(IS_LEGENDS_WALLET ? { explainedFee: undefined, sponsorship: undefined, isQuoteReady: false } : {}),
    draftRevision: (global.currentTransfer.draftRevision ?? 0) + 1,
  });
});

addActionHandler('submitTransferConfirm', (global, actions) => {
  if (global.currentTransfer.isLoading || global.currentTransfer.state !== TransferState.Confirm
    || (IS_LEGENDS_WALLET && !global.currentTransfer.isOfframp && !global.currentTransfer.isQuoteReady)) return global;

  const transfer = global.currentTransfer;
  if (IS_LEGENDS_WALLET && !transfer.isOfframp && transfer.sponsorship?.expiresAt
    && getTransferQuoteRefreshDelay(transfer.sponsorship.expiresAt) === 0) {
    actions.submitTransferInitial({
      tokenSlug: transfer.tokenSlug, toAddress: transfer.toAddress!, amount: transfer.amount!,
      comment: transfer.comment, shouldEncrypt: transfer.shouldEncrypt,
      binPayload: transfer.binPayload, stateInit: transfer.stateInit,
    });
    return undefined;
  }

  const { tokenSlug } = global.currentTransfer;
  const chain = getChainBySlug(tokenSlug);

  if (selectIsHardwareAccount(global)) {
    global = resetHardware(global, chain);
    global = updateCurrentTransfer(global, { state: TransferState.ConnectHardware });
  } else if (selectIsEnclaveSessionValid(global)) {
    global = updateCurrentTransfer(global, { isLoading: true });
    actions.submitTransfer({ enclaveToken: selectEnclaveToken(global) });
  } else {
    global = updateCurrentTransfer(global, { state: TransferState.Password });
  }

  return global;
});

addActionHandler('clearTransferError', (global) => {
  setGlobal(updateCurrentTransfer(global, { error: undefined }));
});

addActionHandler('dismissTransferScamWarning', (global) => {
  global = updateCurrentTransfer(global, { scamWarningType: undefined });
  setGlobal(global);
});

addActionHandler('showTransferScamWarning', (global, actions, { type }) => {
  global = updateCurrentTransfer(global, { scamWarningType: type });
  setGlobal(global);
});
